/**
 * La pantalla de adentro: MIS PRÓXIMOS PARTIDOS y a dónde ir.
 *
 * LA PREGUNTA CON LA QUE SE ABRE ESTA APLICACIÓN ES «¿CUÁNDO JUEGO?», y hasta
 * hace poco la respuesta estaba repartida: había que acordarse de en cuántos
 * torneos está uno inscrito, entrar a cada uno, y buscarse en el cuadro.
 *
 * DEBAJO, LOS ACCESOS RÁPIDOS EN CUADROS. Eran tres renglones de texto
 * apilados, que a la vista son un párrafo: para elegir uno había que leer los
 * tres. En cuadros de dos columnas, con el dibujo arriba, se eligen mirando.
 *
 * Y LLEVAN AVISO CUANDO ALGO ESPERA. Un número sobre «Clubes» es lo único de
 * esta pantalla que le pide algo a quien la mira, y es lo que hace que el
 * cuadro sirva para algo más que navegar: sin él, saber que hay solicitudes
 * esperando obliga a entrar club por club.
 *
 * SE VEN EN LA MISMA CAJA QUE EN EL CUADRO, `MatchBox`, y no en un formato
 * propio. Un partido tiene que verse igual en todas partes: quien ya sabe
 * leer el cuadro no tiene que aprender otra cosa acá, y quien anota desde
 * acá anota con los mismos campos.
 *
 * POR AHORA SOLO HAY PARTIDOS DE TORNEO. Los retos —amistosos y por ranking—
 * todavía no se guardan como partido en ningún lado; el día que se guarden
 * entran acá, y por eso cada fila ya viene diciendo de qué tipo es.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Community, MatchScore, Novelties, UpcomingMatch } from '@tennisup/contracts';
import { MAX_EN_EL_GLOBITO } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { AppLayout } from '../components/AppLayout';
import { ActivitySection } from '../components/ActivitySection';
import { MatchBox } from '../components/Bracket';
import { ScoreEditor, sidesOfMatch } from '../components/ScoreEditor';
import { ChallengeIcon, CommunityIcon, PersonIcon, TrophyIcon } from '../components/icons';
import { useAuth } from '../auth/AuthContext';
import { useFrase } from '../frases/useFrase';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { useFeatures } from '../hooks/useFeatures';
import { últimasVisitas } from '../lib/visitas';

export function HomePage(): JSX.Element {
  const { t } = useTranslation();
  const frase = useFrase();
  const { session } = useAuth();
  const features = useFeatures();
  const [agenda, setAgenda] = useState<UpcomingMatch[] | null>(null);
  const [clubes, setClubes] = useState<Community[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [anotando, setAnotando] = useState<UpcomingMatch | null>(null);
  /*
   * CUÁNTAS COSAS NUEVAS HAY EN CADA ACCESO RÁPIDO. Javier: «poner un círculo
   * en rojo la cantidad de cosas nuevas que hayan para que los usuarios abran
   * los accesos rápidos».
   *
   * ARRANCA EN CERO Y NO EN `null`: así el inicio se dibuja igual que siempre
   * mientras la consulta viaja, y los globitos aparecen cuando llegan. Al
   * revés —esperando el número para dibujar— una consulta lenta retrasaría la
   * pantalla entera por un adorno.
   */
  const [novedades, setNovedades] = useState<Novelties>({
    memes: 0,
    challenges: 0,
    posts: 0,
    tournaments: 0,
  });

  const load = useCallback(async () => {
    try {
      setAgenda(await api.myUpcomingMatches());
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }, [t]);

  const miId = session?.user.id;

  /*
   * LOS GLOBITOS, EN SU PROPIO EFECTO Y CALLADO.
   *
   * SI FALLA NO SE DICE NADA. Un error acá es «no se pudieron contar las
   * novedades», y poner una alerta roja en el inicio por eso sería darle a un
   * adorno el peso de un problema. La pantalla funciona igual sin números.
   */
  useEffect(() => {
    if (miId === undefined) return;
    let vivo = true;
    void api
      .novelties(últimasVisitas(miId))
      .then((n) => {
        if (vivo) setNovedades(n);
      })
      .catch(() => {
        /* Sin números, sin globitos. Ver arriba. */
      });
    return () => {
      vivo = false;
    };
  }, [miId]);

  /**
   * LOS CLUBES SE PIDEN APARTE Y SIN QUEJARSE.
   *
   * Es lo que llena el número de al lado de «Clubes» y el aviso de
   * solicitudes esperando. Si falla, los cuadros siguen sirviendo para lo que
   * más se usan —llevar a algún lado— así que un error rojo por una consulta
   * de adorno sería peor que un cuadro sin número.
   */
  const loadClubes = useCallback(async () => {
    try {
      setClubes(await api.listMyCommunities());
    } catch {
      setClubes([]);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadClubes();
  }, [load, loadClubes]);

  /**
   * Después de anotar se vuelve a pedir la lista ENTERA.
   *
   * El servidor devuelve los partidos de ESE torneo, pero lo que cambió acá
   * es más que eso: el partido anotado se cae de la agenda y el de la ronda
   * siguiente puede aparecer. Reconstruir eso a mano en la pantalla sería
   * tener dos ideas distintas de qué es «lo próximo».
   */
  async function guardar(
    fila: UpcomingMatch,
    score: MatchScore,
    noShowEntryId: string | null,
  ): Promise<void> {
    await api.saveMatchScore(fila.tournamentId, fila.match.id, score, noShowEntryId);
    setAnotando(null);
    await load();
  }

  /** La foto del partido. Aparte del marcador: son dos cosas distintas. */
  async function guardarFoto(fila: UpcomingMatch, file: File | null): Promise<void> {
    if (file === null) await api.removeMatchPhoto(fila.tournamentId, fila.match.id);
    else await api.setMatchPhoto(fila.tournamentId, fila.match.id, file);
    await load();
  }

  const míos = clubes.filter((club) => club.myMembershipStatus === 'ACTIVE');
  // Las solicitudes de TODOS mis clubes juntas: el número de al lado de
  // «Clubes» contesta «¿tengo algo que responder?», sin decir en cuál.
  const esperando = míos.reduce((total, club) => total + club.pendingRequests, 0);

  return (
    <AppLayout>
      {/* EL SALUDO, EN SU PROPIA FRANJA. Es lo primero que se ve al entrar y
          lo único de la pantalla que no hay que leer: dice de quién es esto
          antes de que el ojo baje a lo que hay que hacer.

          Debajo decía «Esto es lo tuyo por ahora.», una frase que tenía
          sentido el día que se escribió y ninguno después. Ahora sale una
          frase de tenis distinta cada vez que se entra; mientras carga —o si
          no carga— no sale nada, que es como se veía antes. */}
      <div className="home-hello">
        <h1>{t('home.title', { name: session?.user.displayName ?? '' })}</h1>
        {frase !== null && <p>{frase}</p>}
      </div>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <section>
        <h2>{t('home.upcomingTitle')}</h2>

        {agenda === null ? (
          /*
           * «CARGANDO…» SOLO MIENTRAS DE VERDAD ESTÁ CARGANDO.
           *
           * Si la petición falló, la lista se queda en nulo para siempre y
           * antes eso dejaba un «Cargando…» eterno DEBAJO del mensaje de
           * error: la pantalla decía a la vez que algo salió mal y que
           * siguiera esperando. Con el error puesto, la lista no dice nada.
           */
          error === null ? <p className="help">{t('common.loading')}</p> : null
        ) : agenda.length === 0 ? (
          /* Sin partidos NO se ofrece a dónde ir con dos enlaces sueltos: los
             cuadros de abajo son exactamente eso, y mejor. */
          <p className="help">{t('home.noMatches')}</p>
        ) : (
          <ul className="upcoming-list">
            {agenda.map((fila) => (
              <li key={fila.match.id}>
                {/* DE QUÉ TORNEO ES, arriba del partido. En una lista que
                    junta varios torneos, una caja de partido suelta no se
                    puede leer: los mismos dos nombres pueden estar jugando
                    en dos lados. */}
                <p className="upcoming-where">
                  <Link to={`/torneos/${fila.tournamentId}`}>{fila.tournamentName}</Link>
                  <span className="help"> · {fila.communityName}</span>
                </p>

                <MatchBox
                  match={fila.match}
                  tournamentName={fila.tournamentName}
                  // QUIÉN PUEDE ANOTAR LO DECIDE `MatchBox` mirando
                  // `match.canScore`, que lo llena el servidor. Acá había una
                  // segunda comprobación igual; se quitó porque no había forma
                  // de escribir una prueba que fallara al sacarla, y una regla
                  // escrita en dos lados es una que tarde o temprano dice dos
                  // cosas distintas.
                  onScore={() => setAnotando(fila)}
                  onSchedule={undefined}
                />

                {anotando?.match.id === fila.match.id && (
                  <ScoreEditor
                    {...sidesOfMatch(fila.match)}
                    onSave={(score, noShow) => guardar(fila, score, noShow)}
                    onPhoto={(file) => guardarFoto(fila, file)}
                    onCancel={() => setAnotando(null)}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="section-label">{t('home.quickTitle')}</h2>

        <ul className="quick-grid">
          {/* EL TABLÓN VA PRIMERO, ANTES QUE MEMES, y lo pidió Javier así:
              «en accesos rápidos para que lo vean incluso va antes de memes».

              Tiene sentido para lo que es: un aviso de «busco partido» solo
              sirve si alguien lo ve a tiempo, y el que lo publicó no puede
              hacer nada más para que lo vean. Memes se abre igual; esto hay
              que encontrarlo. */}
          <QuickCard
            to="/tablon"
            icon={<span aria-hidden="true">📣</span>}
            title={t('posts.title')}
            note={t('posts.hint')}
            badge={novedades.posts}
            badgeLabel={t('home.newCount', { count: novedades.posts })}
            /* Javier: «es lo mas importante de la app, dejarlo siempre
               resaltado para que la gente al ingresar lo vea y llame su
               atención siempre». SIEMPRE: no depende de que haya avisos
               nuevos. Ver `destacado` en `QuickCard`. */
            destacado
          />
          {/* MEMES VA SEGUNDO, y antes iba primero, como lo había pedido Javier. Y tiene sentido: es lo
              único de acá que no es una gestión —clubes, torneos, retos, perfil
              son todos «hacé algo»— y lo que hace que alguien abra la
              aplicación un martes sin partido.

              Y SE PUEDE APAGAR desde la administración de Tennis Up Club. Apagado no
              se dibuja la tarjeta Y la ruta manda al inicio: dejar la tarjeta
              escondida pero la dirección viva es apagar algo a medias, y quien
              tenga el enlace guardado entra igual. */}
          {features.memes && (
            <QuickCard
              to="/memes"
              icon={<span aria-hidden="true">😂</span>}
              title={t('memes.title')}
              note={t('memes.hint')}
              badge={novedades.memes}
              badgeLabel={t('home.newCount', { count: novedades.memes })}
            />
          )}
          <QuickCard
            to="/clubes"
            icon={<CommunityIcon title="" size={24} />}
            title={t('nav.communities')}
            note={
              míos.length === 0 ? t('home.noClubs') : t('home.clubCount', { count: míos.length })
            }
            badge={esperando}
            badgeLabel={t('join.waiting', { count: esperando })}
          />
          <QuickCard
            to="/torneos"
            icon={<TrophyIcon title="" size={24} />}
            title={t('nav.tournaments')}
            note={t('home.tournamentsHint')}
            badge={novedades.tournaments}
            badgeLabel={t('home.newCount', { count: novedades.tournaments })}
          />
          <QuickCard
            to="/retos"
            icon={<ChallengeIcon title="" size={24} />}
            title={t('nav.challenges')}
            note={t('home.challengesHint')}
            badge={novedades.challenges}
            badgeLabel={t('home.newCount', { count: novedades.challenges })}
          />
          {/* LAS CANCHAS van antes del perfil: el perfil se abre una vez y
              esto se abre cada vez que hay que decidir dónde jugar. */}
          <QuickCard
            to="/canchas"
            icon={<span aria-hidden="true">🎾</span>}
            title={t('courts.title')}
            note={t('courts.hint')}
          />
          <QuickCard
            to="/perfil"
            icon={<PersonIcon title="" size={24} />}
            title={t('nav.profile')}
            note={t('home.profileHint')}
          />
        </ul>
      </section>

      {/*
        LO QUE PASÓ EN MIS CLUBES, de a diez.

        Va DEBAJO de los accesos rápidos y no arriba: los accesos son cuatro
        cuadros que se leen de un golpe, y el muro es una lista larga que
        invita a quedarse. Con el muro primero, los accesos quedarían a diez
        pantallas del lugar donde la aplicación arranca, y son lo único que
        sirve para ir a otro lado.

        ES EL MISMO COMPONENTE QUE EN RETOS, con las mismas tarjetas, los
        mismos comentarios y las mismas reacciones. Una segunda versión «para
        el inicio» sería la que se queda sin el arreglo la próxima vez.
      */}
      <section>
        <h2 className="section-label">{t('home.wallTitle')}</h2>
        <ActivitySection />
      </section>
    </AppLayout>
  );
}

/**
 * UN ACCESO, COMO CUADRO.
 *
 * EL AVISO ES UN NÚMERO Y NO UN PUNTO porque «tres solicitudes» y «una» se
 * responden distinto: con un punto hay que entrar para saber cuánto es.
 *
 * Y VIAJA TAMBIÉN EN EL NOMBRE DEL ENLACE, no solo dibujado en la esquina:
 * quien no ve la pantalla oiría «Clubes» a secas y se perdería justamente lo
 * único que le estaba pidiendo algo.
 */
function QuickCard({
  to,
  icon,
  title,
  note,
  badge = 0,
  badgeLabel = '',
  destacado = false,
}: {
  to: string;
  icon: JSX.Element;
  title: string;
  note: string;
  badge?: number;
  badgeLabel?: string;
  /**
   * LA TARJETA PINTADA, que es una sola y siempre la misma.
   *
   * NO ES UN AVISO. El `badge` de arriba dice «pasó algo, mirá»; esto dice
   * «esto es lo que la aplicación hace», y por eso no se apaga cuando no hay
   * novedades. Ver el bloque de `.quick-card-hero` en styles-people.css.
   *
   * SOLO CAMBIA CÓMO SE VE, no qué se anuncia: un lector de pantalla lee la
   * misma tarjeta que antes. Lo resaltado es para el ojo, y anunciarlo
   * —«destacado, Busco partido»— sería contar un detalle de pintura a quien
   * no la ve.
   */
  destacado?: boolean;
}): JSX.Element {
  return (
    <li>
      <Link
        className={destacado ? 'quick-card quick-card-hero' : 'quick-card'}
        to={to}
        {...(badge > 0 ? { 'aria-label': `${title} — ${badgeLabel}` } : {})}
      >
        <span className="quick-icon" aria-hidden="true">
          {icon}
        </span>
        <strong className="quick-title">{title}</strong>
        <span className="quick-note">{note}</span>
        {badge > 0 && (
          /* EL «9+» SALE DEL CONTRATO y no de un `> 9` escrito acá: era una
             copia de la misma regla, y dos lugares que deciden lo mismo son
             dos que un día dicen distinto. Ver `MAX_EN_EL_GLOBITO`. */
          <span className="quick-badge" aria-hidden="true">
            {badge > MAX_EN_EL_GLOBITO ? `${MAX_EN_EL_GLOBITO}+` : badge}
          </span>
        )}
      </Link>
    </li>
  );
}
